import React from 'react';
import innerText from 'react-innertext';
import CONFIG from '../../../configs/config';
import I18nString from '../I18nString';

const OK = <I18nString>label.ok</I18nString>,
    CANCEL = <I18nString>label.cancel</I18nString>;

/**
 * Shows an alert dialog with the given message. If no title is given, the
 * message is used as the title as well. If the native dialog is disabled,
 * a regular alert box is shown instead.
 *
 * @param {String} message The message to show in the dialog.
 * @param {String} [title] The title of the dialog. If not given, the message
 *                         is used as the title as well.
 * @param {String} [buttonLabel] The label of the "OK" button. If not given,
 *                               the default label is used.
 * @param {Object} [otherProps] Additional properties to pass to the dialog
 *
 * @returns {Promise} A promise that resolves when the dialog is closed.
 */
export async function dialogAlert(message, title, buttonLabel, otherProps = {}) {
    if (CONFIG.disableNonNativeDialogs) return alert(innerText(message));

    return new Promise(resolve => {
        app.store.dispatch(app.actions.ui.dialog.show(resolve, message, title, [buttonLabel || OK], otherProps));
    });
}

/**
 * Shows a confirm dialog with the given message. If no title is given, the
 * message is used as the title as well. If the native dialog is disabled,
 * a regular confirm box is shown instead.
 *
 * @param {String} message The message to show in the dialog.
 * @param {String} [title] The title of the dialog. If not given, the message
 *                         is used as the title as well.
 * @param {String} [acceptString] The label of the accept button. If not given,
 *                               the default label is used.
 * @param {String} [rejectString] The label of the reject button. If not given,
 *                               the default label is used.
 * @param {Object} [otherProps] Additional properties to pass to the dialog
 *
 * @returns {Promise<Boolean>} A promise that resolves to true if the
 *                             accept button was clicked, false if the
 *                             reject button was clicked.
 */
export async function dialogConfirm(message, title = null, acceptString = OK, rejectString = CANCEL, otherProps = {}) {
    if (CONFIG.disableNonNativeDialogs) {
        return confirm(innerText(message));
    }

    return new Promise(resolve => {
        function handleResponse(response) {
            resolve(response == acceptString);
        }
        app.store.dispatch(app.actions.ui.dialog.show(handleResponse, message, title, [acceptString, rejectString], otherProps));
    });
}

/**
 * Shows a dialog with multiple options.
 *
 * @param {String} message The message to show in the dialog.
 * @param {String} [title] The title of the dialog. If not given, the message
 *                         is used as the title as well.
 * @param {Array<String>} options The options to show in the dialog.
 * @param {Object} [otherProps] Additional properties to pass to the dialog
 *
 * @returns {Promise<String>} A promise that resolves to the selected option.
 */
export async function dialogMultipleOptions(message, title, options, otherProps = {}) {
    if (CONFIG.disableNonNativeDialogs) {
        // since there's no native support for this, ask one time for each option (no UI issues since the fallback is only used on puppeteer tests)
        return options.find((option, i) => confirm(`${message}: ${option} (${i + 1}/${options.length})`));
    }

    return new Promise(resolve => {
        app.store.dispatch(app.actions.ui.dialog.show(resolve, message, title, options, otherProps));
    });
}
